import { Capacitor, registerPlugin } from '@capacitor/core'
import { normalizeIndianMobile } from '../../../lib/phoneNormalize'
import { debugLog } from '../../../lib/debugLog'

export interface DeviceCallEntry {
  number: string
  normalizedNumber: string  // 10-digit normalized form
  direction: 'inbound' | 'outbound'
  started_at: Date
  duration_s: number
}

interface CallLogPermissions {
  callLog: 'granted' | 'denied' | 'prompt'
  mediaAudio: 'granted' | 'denied' | 'prompt'
  storage: 'granted' | 'denied' | 'prompt'
}

interface CallLogPlugin {
  requestPermissions(): Promise<CallLogPermissions>
  checkPermissions(): Promise<CallLogPermissions>
  query(options: { sinceMs: number }): Promise<{ rows: Array<{ number: string; type: string; date: string; duration: string }> }>
  queryRecentAudio(options: { sinceMs: number }): Promise<{ files: Array<{ path: string; displayName: string; dateAdded: number; size: number }> }>
}

const CallLog = registerPlugin<CallLogPlugin>('CallLog')

/**
 * Request READ_CALL_LOG + READ_MEDIA_AUDIO (Android 13+) / READ_EXTERNAL_STORAGE (≤12).
 * Call this alongside Filesystem.requestPermissions() at app start.
 */
export async function requestCallLogPermissions(): Promise<CallLogPermissions | null> {
  if (!Capacitor.isNativePlatform()) return null
  try {
    return await CallLog.requestPermissions()
  } catch (e) {
    debugLog('CallLog', 'requestPermissions error', String(e))
    return null
  }
}

/**
 * Query Android MediaStore for audio files added after sinceMs.
 * Returns absolute filesystem paths — works for ALL OEMs regardless of folder.
 * Samsung S23 (One UI 5/6) saves recordings here even when folder scan returns empty.
 */
export async function queryMediaStoreAudio(sinceMs: number): Promise<Array<{ path: string; displayName: string; dateAdded: number; size: number }>> {
  if (!Capacitor.isNativePlatform()) return []
  try {
    const { files } = await CallLog.queryRecentAudio({ sinceMs })
    return files
  } catch (e) {
    debugLog('Recording', 'MediaStore query error', String(e))
    return []
  }
}

/**
 * Query Android CallLog for answered calls newer than `since`.
 * Returns [] on iOS or when READ_CALL_LOG is not granted.
 */
export async function readAndroidCallLog(since: Date): Promise<DeviceCallEntry[]> {
  if (!Capacitor.isNativePlatform()) return []

  try {
    const { rows } = await CallLog.query({ sinceMs: since.getTime() })
    return rows
      .filter(r => r.type === '1' || r.type === '2')
      .map(r => ({
        number: r.number,
        normalizedNumber: normalizeIndianMobile(r.number),
        direction: (r.type === '1' ? 'inbound' : 'outbound') as 'inbound' | 'outbound',
        started_at: new Date(parseInt(r.date, 10)),
        duration_s: parseInt(r.duration, 10),
      }))
  } catch (e) {
    debugLog('CallLog', 'query error', String(e))
    return []
  }
}

/**
 * Find a call log entry that matches a recording file's mtime (±30 s).
 */
export function matchCallToRecording(
  calls: DeviceCallEntry[],
  fileMtime: number,
  windowMs = 30_000,
): DeviceCallEntry | null {
  const match = calls.find(c => Math.abs(c.started_at.getTime() - fileMtime) <= windowMs) ?? null
  return match
}
